import { Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controls } from '../../components/controls/Controls';
import { Form, useForm } from '../../components/useForm';
import http from '../../http-common';

const initialFieldValues = {
    id: 0,
    idBanco: '',
    numeroCuenta: '',
    clabe: '',
    tipoCuenta: '',
    titular: '',
    moneda: '',
    saldoInicial: 0,
    activa: true,
    cuentaDefault: false,
};

export default function CuentasBancariasForme(props) {
    const { addOrEdit, recordForEdit, bancos } = props;

    const bancosOptions = (bancos || []).map((b) => ({ id: b.id, title: b.nombre }));

    const [saldoCalculado, setSaldoCalculado] = useState(0);
    const [saldoInicialError, setSaldoInicialError] = useState('');

    const {
        values,
        setValues,
        errors,
        setErrors,
        handleInputChange,
        resetForm,
        // eslint-disable-next-line no-use-before-define
    } = useForm(initialFieldValues, true, validate);

    function validate(fieldValues = values) {
        const temp = { ...errors };

        if ('idBanco' in fieldValues) {
            temp.idBanco = fieldValues.idBanco ? '' : 'Campo Requerido';
        }
        if ('numeroCuenta' in fieldValues) {
            temp.numeroCuenta = fieldValues.numeroCuenta ? '' : 'Campo Requerido';
        }
        if ('titular' in fieldValues) {
            temp.titular = fieldValues.titular ? '' : 'Campo Requerido';
        }

        setErrors({ ...temp });

        if (fieldValues === values) {
            return Object.values(temp).every((x) => x === '');
        }
        return true;
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            addOrEdit(values, resetForm);
        }
    };

    useEffect(() => {
        setSaldoCalculado(0);
        setSaldoInicialError('');
        if (recordForEdit != null) {
            setValues({ ...recordForEdit });
            if (recordForEdit.id) {
                (async () => {
                    try {
                        const resp = await http.get(
                            `/movimientos-bancarios/calcula-saldo-actual/${recordForEdit.id}`,
                            { validateStatus: () => true },
                        );
                        if (resp?.status === 200 && resp?.data?.saldoCalculado != null) {
                            setSaldoCalculado(resp.data.saldoCalculado);
                            setSaldoInicialError('');
                        } else if (resp?.status === 404 || resp?.status === 400 || !resp) {
                            setSaldoCalculado(0);
                            setSaldoInicialError('Necesitas definir un saldo inicial para poder calcular el saldo actual. Después de que lo guardes se calculará.');
                        } else {
                            setSaldoCalculado(0);
                        }
                    } catch {
                        setSaldoCalculado(0);
                    }
                })();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordForEdit]);

    const formatMoneda = (value) => {
        if (value == null) return '$ 0.00';
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2,
        }).format(value);
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Grid container>
                <Grid item xs={6}>
                    <Controls.Select
                        error={errors.idBanco}
                        label='Banco'
                        name='idBanco'
                        onChange={handleInputChange}
                        options={bancosOptions}
                        value={values.idBanco}
                    />
                    <Controls.Input
                        error={errors.numeroCuenta}
                        label='Número de Cuenta'
                        name='numeroCuenta'
                        onChange={handleInputChange}
                        value={values.numeroCuenta}
                    />
                    <Controls.Input
                        error={errors.clabe}
                        label='CLABE / IBAN'
                        name='clabe'
                        onChange={handleInputChange}
                        value={values.clabe}
                    />
                    <Controls.Input
                        error={errors.tipoCuenta}
                        label='Tipo de Cuenta'
                        name='tipoCuenta'
                        onChange={handleInputChange}
                        value={values.tipoCuenta}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Controls.Input
                        error={errors.titular}
                        label='Titular'
                        name='titular'
                        onChange={handleInputChange}
                        value={values.titular}
                    />
                    <Controls.Input
                        error={errors.moneda}
                        label='Moneda'
                        name='moneda'
                        onChange={handleInputChange}
                        value={values.moneda}
                    />
                    <Controls.Input
                        inputProps={{ step: '0.01' }}
                        label='Saldo Inicial'
                        name='saldoInicial'
                        onBlur={(e) => {
                            if (e.target.value === '' || e.target.value === null) {
                                handleInputChange({ target: { name: 'saldoInicial', value: 0 } });
                            }
                        }}
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                            if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                        }}
                        sx={{
                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                            '& input[type=number]': { MozAppearance: 'textfield' },
                        }}
                        type='number'
                        value={values.saldoInicial}
                    />
                    {saldoInicialError && (
                        <Typography
                            color='error'
                            sx={{
                                fontSize: '0.78rem',
                                mb: 1,
                                mt: -0.5,
                                px: 1,
                            }}
                        >
                            {saldoInicialError}
                        </Typography>
                    )}
                    <Controls.Input
                        disabled
                        label='Saldo Actual'
                        name='saldoActual'
                        onChange={() => {}}
                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                        value={formatMoneda(saldoCalculado)}
                    />
                    <Controls.Checkbox
                        label='Activa'
                        name='activa'
                        onChange={handleInputChange}
                        value={values.activa}
                    />
                    <Controls.Checkbox
                        label='Cuenta Default'
                        name='cuentaDefault'
                        onChange={handleInputChange}
                        value={values.cuentaDefault}
                    />
                    <div>
                        <Controls.Button text='Guardar' type='submit' />
                        <Controls.Button
                            color='default'
                            onClick={resetForm}
                            text='Reset'
                        />
                    </div>
                </Grid>
            </Grid>
        </Form>
    );
}
